import { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { updateOrganization, updateOrganizationBilling, updateProfile } from '../services/ApiService';
import { isValidSwedishBankAccount, isValidSwedishOrganizationNumber } from '../utils/swedishValidation';
import { Building2, User, Crown, Receipt } from 'lucide-react';
import { PRICING_TIERS, PricingTier } from '../constants/pricing';

const freeTier: PricingTier = { name: 'Gratis provperiod', price: '0 kr/mån', members: 'Obegränsat under provperioden' };

export default function SettingsPage() {
  const { currentOrg, isBoardMember, refreshOrganizations } = useOrganization();
  const { user, refreshProfile } = useAuth();
  const [orgName, setOrgName] = useState(currentOrg?.name ?? '');
  const [orgDesc, setOrgDesc] = useState(currentOrg?.description ?? '');
  const [orgNumber, setOrgNumber] = useState(currentOrg?.orgNumber ?? '');
  const [billingBank, setBillingBank] = useState(currentOrg?.billingBank ?? '');
  const [billingAccount, setBillingAccount] = useState(currentOrg?.billingAccount ?? '');
  const [billingPaymentDays, setBillingPaymentDays] = useState(currentOrg?.billingPaymentDays?.toString() ?? '');
  const [paymentRemindersEnabled, setPaymentRemindersEnabled] = useState(currentOrg?.paymentRemindersEnabled ?? true);
  const [paymentReminderDays, setPaymentReminderDays] = useState(currentOrg?.paymentReminderDays?.toString() ?? '7');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [orgSuccess, setOrgSuccess] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [orgNumberError, setOrgNumberError] = useState<string | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [billingAccountError, setBillingAccountError] = useState<string | null>(null);

  const validateOrgNumber = (value: string): boolean => {
    if (!isValidSwedishOrganizationNumber(value)) {
      setOrgNumberError('Ogiltigt organisationsnummer');
      return false;
    }
    setOrgNumberError(null);
    return true;
  };

  const validateBillingAccount = (value: string): boolean => {
    if (!value) {
      setBillingAccountError(null);
      return true;
    }
    if (!isValidSwedishBankAccount(value)) {
      setBillingAccountError('Ogiltigt kontonummer, PG- eller BG-nummer');
      return false;
    }
    setBillingAccountError(null);
    return true;
  };

  const handleOrgSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;
    setOrgError(null);
    if (!validateOrgNumber(orgNumber)) return;

    setSavingOrg(true);
    setOrgSuccess(false);
    try {
      await updateOrganization(currentOrg.id, { name: orgName, description: orgDesc, orgNumber });
      await refreshOrganizations();
      setOrgSuccess(true);
      setTimeout(() => setOrgSuccess(false), 3000);
    } catch (err: unknown) {
      setOrgError(err instanceof Error ? err.message : 'Kunde inte spara föreningsinformationen.');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleBillingSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg) return;

    const isBillingAccountValid = validateBillingAccount(billingAccount);
    if (!isBillingAccountValid) return;

    setSavingBilling(true);
    setBillingSuccess(false);
    try {
      await updateOrganizationBilling(currentOrg.id, {
        billingBank: billingBank || null,
        billingAccount: billingAccount || null,
        billingPaymentDays: billingPaymentDays ? parseInt(billingPaymentDays, 10) : null,
        paymentRemindersEnabled,
        paymentReminderDays: parseInt(paymentReminderDays, 10) || 7,
      });
      await refreshOrganizations();
      setBillingSuccess(true);
      setTimeout(() => setBillingSuccess(false), 3000);
    } catch {
      alert('Kunde inte spara faktureringsuppgifterna.');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSavingProfile(true);
    setProfileSuccess(false);
    try {
      await updateProfile({ fullName, phone });
      await refreshProfile();
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch {
      alert('Kunde inte spara profilen.');
    } finally {
      setSavingProfile(false);
    }
  };

  const tier = PRICING_TIERS[currentOrg?.subscriptionTier ?? 'free'] ?? freeTier;
  const trialEnd = currentOrg?.trialEndsAt ? new Date(currentOrg.trialEndsAt) : null;
  const isInTrial = trialEnd && trialEnd > new Date();

  return (
    <div className="max-w-2xl mx-auto p-6 lg:p-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Inställningar</h1>
        <p className="text-gray-500 text-sm mt-1">Hantera förening och profil</p>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
            <User className="w-5 h-5 text-teal-700" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Min profil</h2>
        </div>
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Namn</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={savingProfile} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm disabled:opacity-50">
              {savingProfile ? 'Sparar...' : 'Spara profil'}
            </button>
            {profileSuccess && <span className="text-sm text-green-600">Sparat!</span>}
          </div>
        </form>
      </section>

      {isBoardMember && (
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-teal-700" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Foreningens uppgifter</h2>
          </div>
          <form onSubmit={handleOrgSave} className="space-y-4">
            {orgError && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">{orgError}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Foreningsnamn</label>
              <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
              <textarea value={orgDesc} onChange={e => setOrgDesc(e.target.value)} rows={3} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Organisationsnummer</label>
              <input
                type="text"
                value={orgNumber}
                onChange={e => { setOrgNumber(e.target.value); if (orgNumberError) setOrgNumberError(null); }}
                onBlur={e => validateOrgNumber(e.target.value)}
                required
                placeholder="XXXXXX-XXXX"
                className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${orgNumberError ? 'border-red-300' : 'border-gray-200'}`}
              />
              {orgNumberError && <p className="text-sm text-red-600 mt-1">{orgNumberError}</p>}
            </div>
            <div className="flex items-center gap-3">
              <button data-tour="org-save" type="submit" disabled={savingOrg} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm disabled:opacity-50">
                {savingOrg ? 'Sparar...' : 'Spara'}
              </button>
              {orgSuccess && <span className="text-sm text-green-600">Sparat!</span>}
            </div>
          </form>
        </section>
      )}

      {isBoardMember && (
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-teal-700" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Faktureringsuppgifter</h2>
          </div>
          <form onSubmit={handleBillingSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Bank</label>
              <input type="text" value={billingBank} onChange={e => setBillingBank(e.target.value)} placeholder="t.ex. Nordea, SEB, Handelsbanken" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Kontonummer / PG / BG</label>
              <input
                type="text"
                value={billingAccount}
                onChange={e => { setBillingAccount(e.target.value); if (billingAccountError) setBillingAccountError(null); }}
                onBlur={e => validateBillingAccount(e.target.value)}
                placeholder="t.ex. 1234-56 789 01"
                className={`w-full px-4 py-2.5 rounded-xl border focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${billingAccountError ? 'border-red-300' : 'border-gray-200'}`}
              />
              {billingAccountError && <p className="text-sm text-red-600 mt-1">{billingAccountError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Betalningsvillkor (dagar)</label>
              <input type="number" min="0" value={billingPaymentDays} onChange={e => setBillingPaymentDays(e.target.value)} placeholder="30" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={paymentRemindersEnabled}
                  onChange={e => setPaymentRemindersEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-teal-700 focus:ring-teal-500"
                />
                Skicka automatisk betalpåminnelse
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">En påminnelse via e-post och SMS till den som inte betalat i tid.</p>
            </div>
            {paymentRemindersEnabled && (
              <div>
                <label htmlFor="payment-reminder-days" className="block text-sm font-medium text-gray-700 mb-1.5">Antal dagar efter förfallodag</label>
                <input id="payment-reminder-days" type="number" min="1" max="90" value={paymentReminderDays} onChange={e => setPaymentReminderDays(e.target.value)} placeholder="7" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                data-tour="billing-save"
                type="submit"
                disabled={savingBilling || !!billingAccountError}
                className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm disabled:opacity-50"
              >
                {savingBilling ? 'Sparar...' : 'Spara'}
              </button>
              {billingSuccess && <span className="text-sm text-green-600">Sparat!</span>}
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
            <Crown className="w-5 h-5 text-amber-700" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Abonnemang</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <p className="font-medium text-gray-900">{tier.name}</p>
              <p className="text-sm text-gray-500">{tier.members}</p>
            </div>
            <p className="font-semibold text-gray-900">{tier.price}</p>
          </div>
          {isInTrial && (
            <p className="text-sm text-amber-700 bg-amber-50 px-4 py-2.5 rounded-xl">
              Provperiod aktiv till {trialEnd.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
          <div className="mt-4 space-y-2">
            <p className="text-xs text-gray-500 mb-3">Nivån bestäms automatiskt av antalet fastigheter vid betalning.</p>
            {Object.entries(PRICING_TIERS).map(([key, t]) => (
              <div
                key={key}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                  key === currentOrg?.subscriptionTier
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-gray-200'
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900 text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.members}</p>
                </div>
                <p className="text-sm font-semibold text-teal-700">{t.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
